import {setCurrentScreen, ScreensEnum, setErrorReason} from '../FuntoneSlice';
import {useDispatch} from 'react-redux';

/**
 * Navigate to a screen with automatic history saving
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} screenName - Screen name from ScreensEnum
 * @param {string} historyValue - Optional custom history value (defaults to screenName.toLowerCase())
 * @param {Object} options - Navigation options
 * @param {boolean} options.scrollToTop - Whether to scroll to top (default: true)
 * @param {Function} options.onBeforeNavigate - Callback before navigation
 * @param {Function} options.onAfterNavigate - Callback after navigation
 */
export const navigateToScreen = (dispatch, screenName, historyValue = null, options = {}) => {
  const {scrollToTop = true, onBeforeNavigate = null, onAfterNavigate = null, noHistorySave = false} = options;

  // Resolve dispatch: accept dispatch function or a store object with dispatch.
  if (!dispatch) {
    try {
      const store =
        typeof window !== 'undefined' &&
        window.EpiserverSpa &&
        window.EpiserverSpa.Context &&
        window.EpiserverSpa.Context.getStore &&
        window.EpiserverSpa.Context.getStore();
      dispatch = store && store.dispatch;
    } catch (e) {
      dispatch = null;
    }
  } else if (dispatch && typeof dispatch.dispatch === 'function') {
    dispatch = dispatch.dispatch;
  }

  if (typeof dispatch !== 'function') {
    if (typeof console !== 'undefined' && console.warn)
      console.warn('navigateToScreen: no dispatch available; navigation aborted for', screenName);
    return false;
  }

  if (onBeforeNavigate) onBeforeNavigate();

  // setCurrentScreen now automatically saves to history, so we don't need to call saveToHistory separately
  const screenPayload = options.params ? { screen: screenName, params: options.params } : screenName;
  dispatch(setCurrentScreen(screenPayload));

  if (scrollToTop && typeof window !== 'undefined') {
    // Use requestAnimationFrame for better iOS compatibility
    requestAnimationFrame(() => {
      // Try multiple scroll methods for better cross-browser/iOS support
      window.scrollTo(0, 0);
      window.scrollTo({top: 0, left: 0, behavior: 'instant'});
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Additional scroll for iOS Safari
      if (document.querySelector('.funtone-main-container')) {
        document.querySelector('.funtone-main-container').scrollTop = 0;
      }
    });
  }

  if (onAfterNavigate) onAfterNavigate();

  return true;
};

/**
 * Quick navigation shortcuts for common screens
 */
export const navigateToCatalog = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.CATALOG, 'catalog', options);
};

export const navigateToSearch = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.SEARCH, 'search', options);
};

export const navigateToSongs = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.SONGS_PAGE, 'songs_page', options);
};

export const navigateToSelectSubscriber = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.SUBSCRIBER_SELECT, 'selectsubscriber', options);
};

export const navigateToError = (dispatch, errorReason = null, options = {}) => {
  if (errorReason) {
    options.onBeforeNavigate = () => {
      dispatch(setErrorReason(errorReason));
    };
  }
  return navigateToScreen(dispatch, ScreensEnum.ERROR, 'error', options);
};

export const navigateToPackageSelect = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.PACKAGE_SELECT, 'package_select', options);
};

export const navigateToAdminPanel = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.ADMIN_PANEL, 'admin_panel', options);
};

export const navigateToSongSettings = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.SONG_SETTINGS, 'songsettings', options);
};

export const navigateToConfigureSongs = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.CONFIGURE_SONGS, 'configuresongs', options);
};

export const navigateToTermsAndConditions = (dispatch, options = {}) => {
  return navigateToScreen(dispatch, ScreensEnum.TERMS_AND_CONDITIONS, 'termsandconditions', options);
};

/**
 * Higher-order component to provide screen navigation utilities
 * @param {React.Component} WrappedComponent
 * @returns {React.Component}
 */
export const withScreenNavigation = (WrappedComponent) => {
  return (props) => {
    const dispatch = useDispatch();
    const screenUtils = {
      navigateToScreen: (screenName, historyValue, options) => navigateToScreen(dispatch, screenName, historyValue, options),
      navigateToCatalog: (options) => navigateToCatalog(dispatch, options),
      navigateToSearch: (options) => navigateToSearch(dispatch, options),
      navigateToSongs: (options) => navigateToSongs(dispatch, options),
      navigateToSelectSubscriber: (options) => navigateToSelectSubscriber(dispatch, options),
      navigateToError: (errorReason, options) => navigateToError(dispatch, errorReason, options),
      navigateToPackageSelect: (options) => navigateToPackageSelect(dispatch, options),
      navigateToAdminPanel: (options) => navigateToAdminPanel(dispatch, options),
      navigateToSongSettings: (options) => navigateToSongSettings(dispatch, options),
      navigateToConfigureSongs: (options) => navigateToConfigureSongs(dispatch, options),
      navigateToTermsAndConditions: (options) => navigateToTermsAndConditions(dispatch, options),
    };

    return <WrappedComponent {...props} screenUtils={screenUtils} />;
  };
};

/**
 * React Hook for screen navigation (for functional components)
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Screen navigation utilities
 */
export const useScreenNavigation = () => {
  const dispatch = useDispatch();
  return {
    navigateToScreen: (screenName, historyValue, options) => navigateToScreen(dispatch, screenName, historyValue, options),
    navigateToCatalog: (options) => navigateToCatalog(dispatch, options),
    navigateToSearch: (options) => navigateToSearch(dispatch, options),
    navigateToSongs: (options) => navigateToSongs(dispatch, options),
    navigateToSelectSubscriber: (options) => navigateToSelectSubscriber(dispatch, options),
    navigateToError: (errorReason, options) => navigateToError(dispatch, errorReason, options),
    navigateToPackageSelect: (options) => navigateToPackageSelect(dispatch, options),
    navigateToAdminPanel: (options) => navigateToAdminPanel(dispatch, options),
    navigateToSongSettings: (options) => navigateToSongSettings(dispatch, options),
    navigateToConfigureSongs: (options) => navigateToConfigureSongs(dispatch, options),
    navigateToTermsAndConditions: (options) => navigateToTermsAndConditions(dispatch, options),
  };
};

// Export ScreensEnum for convenience
export {ScreensEnum} from '../FuntoneSlice';
